import React, { useState, useEffect, useReducer, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import SubscriptionModal from "../../components/SubscriptionModal";
import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Card, CardContent, Typography, Grid, Chip, Divider, Tabs, Tab, TextField, IconButton } from "@material-ui/core";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import BusinessIcon from "@material-ui/icons/Business";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import AccountBalanceIcon from "@material-ui/icons/AccountBalance";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import DeleteIcon from "@material-ui/icons/Delete";

import moment from "moment";

const reducer = (state, action) => {
  if (action.type === "LOAD_INVOICES") {
    const invoices = action.payload.invoices || action.payload;
    const newInvoices = [];

    invoices.forEach((invoice) => {
      const invoiceIndex = state.findIndex((i) => i.id === invoice.id);
      if (invoiceIndex !== -1) {
        state[invoiceIndex] = invoice;
      } else {
        newInvoices.push(invoice);
      }
    });

    return [...state, ...newInvoices];
  }

  if (action.type === "UPDATE_INVOICES") {
    const invoice = action.payload;
    const invoiceIndex = state.findIndex((i) => i.id === invoice.id);

    if (invoiceIndex !== -1) {
      state[invoiceIndex] = invoice;
      return [...state];
    } else {
      return [invoice, ...state];
    }
  }

  if (action.type === "DELETE_INVOICE") {
    const invoiceId = action.payload;

    const invoiceIndex = state.findIndex((i) => i.id === invoiceId);
    if (invoiceIndex !== -1) {
      state.splice(invoiceIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2.5),
    overflowY: "scroll",
    background: "#f7f8fa",
    ...theme.scrollbarStyles,
  },
  adminContent: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    overflowY: "scroll",
    overflowX: "hidden",
    overscrollBehavior: "contain",
    scrollbarGutter: "stable",
    padding: "0 4px 24px 0",
    ...theme.scrollbarStyles,
    [theme.breakpoints.down("sm")]: {
      paddingRight: 2,
      paddingBottom: 18,
    },
  },
  financeTabs: {
    minHeight: 46,
    marginBottom: 18,
    borderBottom: "1px solid #e7e9ee",
    "& .MuiTab-root": {
      minHeight: 46,
      minWidth: 0,
      padding: "8px 18px",
      textTransform: "none",
      fontSize: "0.88rem",
      fontWeight: 700,
      color: "#667085"
    },
    "& .Mui-selected": {
      color: "#1f8f55"
    },
    "& .MuiTabs-indicator": {
      height: 3,
      borderRadius: "3px 3px 0 0",
      backgroundColor: "#1f8f55"
    }
  },
  filterBar: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 20,
    padding: "10px 12px",
    flexWrap: "wrap",
    border: "1px solid #e8eaef",
    borderRadius: 12,
    background: "#fff"
  },
  summaryCard: {
    height: "100%",
    minHeight: 112,
    boxSizing: "border-box",
    borderRadius: 12,
    padding: "14px 15px",
    boxShadow: "0 3px 12px rgba(16, 24, 40, 0.05)"
  },
  tableSurface: {
    width: "100%",
    overflowX: "auto",
    border: "1px solid #e7e9ee",
    borderRadius: 12,
    background: "#fff",
    boxShadow: "0 3px 12px rgba(16, 24, 40, 0.04)",
    "& .MuiTableCell-head": {
      whiteSpace: "nowrap",
      color: "#475467",
      fontSize: "0.75rem",
      fontWeight: 800,
      lineHeight: 1.25,
      padding: "8px 10px",
      borderBottom: "1px solid #e7e9ee"
    },
    "& .MuiTableCell-body": {
      whiteSpace: "nowrap",
      color: "#344054",
      fontSize: "0.75rem",
      lineHeight: 1.25,
      padding: "7px 10px",
      borderBottom: "1px solid #f0f1f3"
    },
    "& .MuiTableRow-root": {
      height: 40
    },
    "& .MuiTableRow-root:hover .MuiTableCell-body": {
      background: "#fafbfc"
    },
    "& .MuiTableRow-root:last-child .MuiTableCell-body": {
      borderBottom: 0
    }
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    marginBottom: 10,
    color: "#263238",
    fontSize: "1rem",
    fontWeight: 800
  },
  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 68,
    padding: "3px 8px",
    borderRadius: 999,
    fontSize: "0.7rem",
    lineHeight: 1.25,
    fontWeight: 700
  }
}));

const buildAdminDateRange = (filter, customStart, customEnd) => {
  const today = moment().format("YYYY-MM-DD");
  if (filter === "today") return { startDate: today, endDate: today };
  if (filter === "month") return {
    startDate: moment().startOf("month").format("YYYY-MM-DD"),
    endDate: today
  };
  if (filter === "lastmonth") return { startDate: moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD"), endDate: moment().subtract(1, "month").endOf("month").format("YYYY-MM-DD") };
  if (filter === "custom" && customStart && customEnd) return { startDate: customStart, endDate: customEnd };
  return {};
};

const Invoices = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchParam, ] = useState("");
  const [invoices, dispatch] = useReducer(reducer, []);
  const [storagePlans, setStoragePlans] = React.useState([]);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [isCompanyExpired, setIsCompanyExpired] = useState(false);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [renewalOptions, setRenewalOptions] = useState(null);
  const [renewalModal, setRenewalModal] = useState(false);
  const [adminData, setAdminData] = useState(null);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminFilter, setAdminFilter] = useState("month");
  const [adminCustomStart, setAdminCustomStart] = useState("");
  const [adminCustomEnd, setAdminCustomEnd] = useState("");
  const [inadimplentesOnly, setInadimplentesOnly] = useState(false);
  const [sortByValue, setSortByValue] = useState(false);
  const [financialOverview, setFinancialOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [ledgerData, setLedgerData] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [reviewData, setReviewData] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewActions, setReviewActions] = useState({});

  // Tabs
  const [activeTab, setActiveTab] = useState(0);
  const [detailTab, setDetailTab] = useState(0);

  // Financial config
  const [financialConfig, setFinancialConfig] = useState({
    taxPercent: 0, taxFixed: 0,
    pixFeePercent: 0, pixFeeFixed: 0,
    cardFeePercent: 0, cardFeeFixed: 0,
    extraCosts: []
  });
  const [configLoading, setConfigLoading] = useState(false);
  const [newCostName, setNewCostName] = useState("");
  const [newCostValue, setNewCostValue] = useState("");

  const loadFinancialOverview = async () => {
    if (!user?.super) return;
    setOverviewLoading(true);
    try {
      const { data } = await api.get("/billing/financial-overview", {
        params: {
          ...buildAdminDateRange(
            adminFilter,
            adminCustomStart,
            adminCustomEnd
          ),
          onlyOverdue: inadimplentesOnly
        }
      });
      setFinancialOverview(data);
    } catch (err) {
      setFinancialOverview(null);
      toastError(err);
    } finally {
      setOverviewLoading(false);
    }
  };

  // Verificar se a empresa está vencida
  useEffect(() => {
    if (user && user.company) {
      const isExpired = user.companyAccess
        ? user.companyAccess.state === "EXPIRED_FINANCE_ONLY"
        : moment().isAfter(moment(user.company.dueDate), "day");
      setIsCompanyExpired(isExpired);
    }
  }, [user]);

  useEffect(() => {
    let active = true;
    api.get("/billing/manual-renewal/options")
      .then(({ data }) => {
        if (active) setRenewalOptions(data);
      })
      .catch(() => {
        if (active) setRenewalOptions(null);
      });
    return () => {
      active = false;
    };
  }, [user?.companyId, user?.company?.dueDate]);

  useEffect(() => {
    if (user?.super) {
      setAdminLoading(true);
      const params = buildAdminDateRange(adminFilter, adminCustomStart, adminCustomEnd);
      api.get("/invoices/admin/all", { params })
        .then(({ data }) => setAdminData(data))
        .catch(() => {})
        .finally(() => setAdminLoading(false));
    }
  }, [user, adminFilter, adminCustomStart, adminCustomEnd]);

  useEffect(() => {
    loadFinancialOverview();
  }, [user, adminFilter, adminCustomStart, adminCustomEnd, inadimplentesOnly]);

  useEffect(() => {
    if (!user?.super || activeTab !== 2 || detailTab !== 0) return;
    setLedgerLoading(true);
    api.get("/billing/ledger", { params: buildAdminDateRange(adminFilter, adminCustomStart, adminCustomEnd) })
      .then(({ data }) => setLedgerData(data))
      .catch(err => {
        setLedgerData(null);
        toastError(err);
      })
      .finally(() => setLedgerLoading(false));
  }, [user, activeTab, detailTab, adminFilter, adminCustomStart, adminCustomEnd]);

  const loadReviews = async () => {
    setReviewLoading(true);
    try {
      const { data } = await api.get("/billing/reviews");
      setReviewData(data);
    } catch (err) {
      setReviewData(null);
      toastError(err);
    } finally {
      setReviewLoading(false);
    }
  };

  useEffect(() => {
    if (user?.super) loadReviews();
  }, [user]);

  useEffect(() => {
    if (user?.super && activeTab === 2 && detailTab === 1) loadReviews();
  }, [activeTab, detailTab]);

  // Load financial config
  useEffect(() => {
    if (user?.super) {
      api.get("/invoices/admin/financialconfig")
        .then(({ data }) => {
          if (data) setFinancialConfig({
            taxPercent: data.taxPercent || 0,
            taxFixed: data.taxFixed || 0,
            pixFeePercent: data.pixFeePercent || 0,
            pixFeeFixed: data.pixFeeFixed || 0,
            cardFeePercent: data.cardFeePercent || 0,
            cardFeeFixed: data.cardFeeFixed || 0,
            extraCosts: data.extraCosts || []
          });
        })
        .catch(() => {});
    }
  }, [user]);

  const parseConfigNumber = (value, max = null) => {
    const parsed = Number(String(value ?? "").trim().replace(",", "."));
    if (!Number.isFinite(parsed) || parsed < 0) return 0;
    return max == null ? parsed : Math.min(parsed, max);
  };

  const handleSaveConfig = async () => {
    setConfigLoading(true);
    try {
      const normalizedConfig = {
        ...financialConfig,
        taxPercent: parseConfigNumber(financialConfig.taxPercent, 100),
        taxFixed: parseConfigNumber(financialConfig.taxFixed),
        pixFeePercent: parseConfigNumber(financialConfig.pixFeePercent, 100),
        pixFeeFixed: parseConfigNumber(financialConfig.pixFeeFixed),
        cardFeePercent: parseConfigNumber(financialConfig.cardFeePercent, 100),
        cardFeeFixed: parseConfigNumber(financialConfig.cardFeeFixed),
        extraCosts: (financialConfig.extraCosts || []).map(cost => ({
          ...cost,
          value: parseConfigNumber(cost.value)
        }))
      };
      await api.post("/invoices/admin/financialconfig", normalizedConfig);
      setFinancialConfig(normalizedConfig);
      toast.success("Configurações salvas com sucesso!");
      await loadFinancialOverview();
    } catch (err) {
      toastError(err);
    } finally {
      setConfigLoading(false);
    }
  };

  const handleAddExtraCost = () => {
    if (!newCostName.trim() || !newCostValue) return;
    setFinancialConfig(prev => ({
      ...prev,
      extraCosts: [
        ...prev.extraCosts,
        { name: newCostName.trim(), value: parseConfigNumber(newCostValue) }
      ]
    }));
    setNewCostName("");
    setNewCostValue("");
  };

  const handleRemoveExtraCost = (idx) => {
    setFinancialConfig(prev => ({
      ...prev,
      extraCosts: prev.extraCosts.filter((_, i) => i !== idx)
    }));
  };

  const formatCents = value =>
    (Number(value || 0) / 100).toLocaleString("pt-br", {
      style: "currency",
      currency: "BRL"
    });

  const handleLedgerExport = async () => {
    try {
      const response = await api.get("/billing/ledger/export", {
        params: buildAdminDateRange(adminFilter, adminCustomStart, adminCustomEnd),
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "billing-ledger.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toastError(err);
    }
  };

  const handleReviewDecision = async item => {
    const action = reviewActions[item.uuid] || "KEEP_ACCESS";
    const note = window.prompt(
      "Descreva o motivo da decisão para a trilha de auditoria:"
    );
    if (!note) return;
    const payload = {
      action,
      note,
      idempotencyKey: `review-${item.uuid}-${Date.now()}-${Math.random()
        .toString(16)
        .slice(2)}`
    };
    if (action === "RECORD_LOSS") {
      const amount = window.prompt("Valor da perda em reais:");
      if (!amount) return;
      payload.adjustmentAmountCents = Math.round(
        Number(String(amount).replace(",", ".")) * 100
      );
    }
    if (action === "ADJUST_PERIOD") {
      const targetDueDate = window.prompt(
        "Novo vencimento proposto (AAAA-MM-DD):"
      );
      if (!targetDueDate) return;
      payload.targetDueDate = targetDueDate;
    }
    try {
      await api.post(
        `/billing/reviews/${item.uuid}/decisions`,
        payload,
        { headers: { "Idempotency-Key": payload.idempotencyKey } }
      );
      toast.success("Decisão registrada na trilha de auditoria.");
      await loadReviews();
    } catch (err) {
      toastError(err);
    }
  };

  const calcLucro = () => {
    const forecast = financialOverview?.forecast || {};
    const providerFees = forecast.providerFeeByMethod || {};
    const periodMonths = Number(financialOverview?.period?.monthCount || 1);
    return {
      gross: Number(forecast.recognizedGrossCents || 0) / 100,
      providerFee: Number(providerFees.totalCents || 0) / 100,
      cashNet: Number(forecast.projectedNetAfterProviderCents || 0) / 100,
      adjustments: Number(forecast.adjustmentsCents || 0) / 100,
      tax: Number(forecast.estimatedTaxCents || 0) / 100,
      extraTotal: Number(forecast.estimatedExtraCostsCents || 0) / 100,
      net: Number(forecast.projectedProfitCents || 0) / 100,
      unknownCashCount: Number(forecast.unknownProviderNetCount || 0),
      usesEstimatedInputs: Boolean(forecast.usesEstimatedInputs),
      periodMonths
    };
  };

  const handleOpenContactModal = (invoices) => {
    setStoragePlans(invoices);
    setSelectedContactId(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(false);
    setRenewalModal(false);
  };

  const handleRenovar = async () => {
    if (creatingInvoice) return;
    const plan = user?.company?.plan;
    if (!plan) {
      toast.error("Nenhum plano encontrado. Entre em contato com o suporte.");
      return;
    }
    if (renewalOptions?.mode === "manual_renewal") {
      if (renewalOptions?.eligibility?.eligible !== true) {
        toast.error("A renovação ainda não está dentro da janela permitida.");
        return;
      }
      setRenewalModal(true);
      setStoragePlans(null);
      setContactModalOpen(true);
      return;
    }
    // Mensal: usa a cobranca emitida quando ela existe. Para empresas recem-
    // criadas, o backend garante de forma idempotente a primeira cobranca no
    // checkout, sempre com o valor oficial do plano/contrato.
    const openInvoice = invoices.find(
      inv =>
        inv.status !== "paid" &&
        String(inv.billingPurchase?.status || "").toUpperCase() !==
          "APPROVED"
    );
    if (openInvoice) {
      setRenewalModal(false);
      handleOpenContactModal(openInvoice);
      return;
    }
    setRenewalModal(false);
    handleOpenContactModal(null);
  };
  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchInvoices = async () => {
        try {
          const { data } = await api.get("/invoices/all", {
            params: { searchParam, pageNumber },
          });
          dispatch({ type: "LOAD_INVOICES", payload: data });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
          setLoading(false);
        }
      };
      fetchInvoices();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const rowStyle = (record) => {
    if (
      String(record.billingPurchase?.status || "").toUpperCase() ===
      "APPROVED"
    ) {
      return { backgroundColor: "#f4fbf5" };
    }
    const hoje = moment(moment()).format("DD/MM/yyyy");
    const vencimento = moment(record.dueDate).format("DD/MM/yyyy");
    var diff = moment(vencimento, "DD/MM/yyyy").diff(moment(hoje, "DD/MM/yyyy"));
    var dias = moment.duration(diff).asDays();
    if (dias < 0 && record.status !== "paid") {
      return { backgroundColor: "#ffbcbc9c" };
    }
  };

  const rowStatus = (record) => {
    if (
      String(record.billingPurchase?.status || "").toUpperCase() ===
      "APPROVED"
    ) {
      return "Pagamento aprovado";
    }
    const hoje = moment(moment()).format("DD/MM/yyyy");
    const vencimento = moment(record.dueDate).format("DD/MM/yyyy");
    var diff = moment(vencimento, "DD/MM/yyyy").diff(moment(hoje, "DD/MM/yyyy"));
    var dias = moment.duration(diff).asDays();
    const status = record.status;
    if (status === "paid") {
      return "Pago";
    }
    if (dias < 0) {
      return "Vencido";
    } else {
      return "Em Aberto"
    }
  }

  const exportToCSV = async () => {
    try {
      const response = await api.get("/billing/financial-overview/export", {
        params: {
          ...buildAdminDateRange(
            adminFilter,
            adminCustomStart,
            adminCustomEnd
          ),
          onlyOverdue: inadimplentesOnly
        },
        responseType: "blob"
      });
      const url = URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `visao_financeira_${moment().format("YYYY-MM-DD")}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toastError(err);
    }
  };

  const getDisplayInvoices = () => {
    let list = adminData?.invoices || [];
    if (inadimplentesOnly) {
      const today = moment();
      list = list.filter(inv => inv.status !== "paid" && moment(inv.dueDate).isSameOrBefore(today, "day"));
    }
    if (sortByValue) {
      list = [...list].sort((a, b) => Number(b.value||0) - Number(a.value||0));
    }
    return list;
  };

  const purchaseIsPaid = status =>
    String(status || "").toUpperCase() === "RECEIVED";

  const purchaseStatusLabel = status => {
    const normalizedStatus = String(status || "").toUpperCase();
    if (purchaseIsPaid(normalizedStatus)) return "✓ Recebido";
    if (normalizedStatus === "PARTIALLY_RECEIVED") {
      return "Parcialmente recebido";
    }
    if (
      [
        "APPROVED",
        "APPROVED_AWAITING_CREDIT",
        "CONFIRMED_AWAITING_CREDIT"
      ].includes(normalizedStatus)
    ) {
      return "Confirmado - aguardando crédito";
    }
    if (normalizedStatus === "NEEDS_REVIEW") return "Em revisão";
    if (normalizedStatus === "OVERDUE") return "Vencido";
    if (["CANCELLED", "CANCELED"].includes(normalizedStatus)) {
      return "Cancelado";
    }
    if (normalizedStatus === "FAILED") return "Falhou";
    return "Em aberto";
  };

  const purchaseStatusColors = status => {
    const normalizedStatus = String(status || "").toUpperCase();
    if (purchaseIsPaid(normalizedStatus)) {
      return { color: "#1b5e20", background: "#e8f5e9" };
    }
    if (
      [
        "PARTIALLY_RECEIVED",
        "APPROVED",
        "APPROVED_AWAITING_CREDIT",
        "CONFIRMED_AWAITING_CREDIT"
      ].includes(normalizedStatus)
    ) {
      return { color: "#0d47a1", background: "#e3f2fd" };
    }
    if (normalizedStatus === "NEEDS_REVIEW") {
      return { color: "#6a1b9a", background: "#f3e5f5" };
    }
    if (["FAILED", "CANCELLED", "CANCELED"].includes(normalizedStatus)) {
      return { color: "#b71c1c", background: "#ffebee" };
    }
    return { color: "#b54708", background: "#fff3e0" };
  };

  const purchaseIsOverdue = purchase =>
    ["OVERDUE", "PAST_DUE"].includes(
      String(purchase.presentationStatus || purchase.purchaseStatus || "")
        .toUpperCase()
    );

  const getDisplayPurchases = () => {
    let list = financialOverview?.purchases || [];
    if (inadimplentesOnly) {
      list = list.filter(purchaseIsOverdue);
    }
    if (sortByValue) {
      list = [...list].sort(
        (a, b) =>
          Number(b.totalValueCents || 0) - Number(a.totalValueCents || 0)
      );
    }
    return list;
  };

  const approvedInstallmentMessage = invoice => {
    const purchase = invoice.billingPurchase;
    if (
      !purchase ||
      String(purchase.status || "").toUpperCase() !== "APPROVED"
    ) {
      return null;
    }
    const installments = Math.max(
      1,
      Number(purchase.selectedInstallments || 1)
    );
    const installmentValue =
      Number(purchase.totalValueCents || 0) / installments / 100;
    return `Pagamento aprovado — ${installments}x de ${installmentValue.toLocaleString(
      "pt-br",
      { style: "currency", currency: "BRL" }
    )} no cartão`;
  };

  const lucro = calcLucro();
  const adminPeriodLabel =
    adminFilter === "today"
      ? "Hoje"
      : adminFilter === "month"
      ? "Este mês"
      : adminFilter === "lastmonth"
      ? "Mês passado"
      : adminFilter === "all"
      ? "Todo o histórico"
      : "Período personalizado";
  const reviewPendingCount = Number(reviewData?.count || 0);
  const purchasesWithCashInPeriod = (financialOverview?.purchases || []).filter(
    purchase => Number(purchase.receivedGrossInPeriodCents || 0) !== 0
  );
  const salesByMethod = financialOverview?.commercial?.salesByMethod || {
    pixCents: 0, pixCount: 0, cardCents: 0, cardCount: 0
  };
  const financialForecast = financialOverview?.forecast || {};
  const providerFeeBreakdown = financialForecast.providerFeeByMethod || {};
  const overdueClientsCount = Number(
    financialOverview?.commercial?.overdueClientsCount || 0
  );
  const revenueContractOk =
    financialOverview?.revenueRecognitionEnabled !== true ||
    (financialOverview?.forecast &&
      Number.isFinite(Number(financialOverview?.commercial?.recognizedGrossCents)) &&
      financialOverview?.commercial?.salesByMethod &&
      financialOverview?.forecast?.configuredProviderFeeByMethod &&
      financialOverview?.forecast?.feeReconciliation);
  const visibleInvoiceCount =
    user?.super && financialOverview?.featureEnabled === true
      ? getDisplayPurchases().length
      : invoices.length;
  return (
    <MainContainer>
      <SubscriptionModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        aria-labelledby="form-dialog-title"
        Invoice={storagePlans}
        contactId={selectedContactId}
        renewal={renewalModal}
      ></SubscriptionModal>
      <MainHeader>
        <Title>Faturas ({visibleInvoiceCount})</Title>
        {(isCompanyExpired ||
          renewalOptions?.eligibility?.eligible === true) && (
          <div style={{
            backgroundColor: "#fff3e0",
            color: "#e65100",
            padding: "14px 18px",
            borderRadius: "8px",
            marginTop: "10px",
            border: "1px solid #ffb74d",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 8
          }}>
            <span>
              <strong>
                {isCompanyExpired
                  ? "Atenção: sua assinatura está vencida."
                  : "Sua renovação já está disponível."}
              </strong>
              {user?.company?.plan && (
                <span> Plano: <strong>{user.company.plan.name}</strong>
                  {" — "}
                  <strong>
                    {renewalOptions?.totalValueCents
                      ? (Number(renewalOptions.totalValueCents) / 100).toLocaleString("pt-br",{style:"currency",currency:"BRL"})
                      : parseFloat(String(user.company.plan.amount).replace(",",".")).toLocaleString("pt-br",{style:"currency",currency:"BRL"})}
                    {renewalOptions?.termMonths > 1
                      ? ` pelo período de ${renewalOptions.termMonths} meses`
                      : "/mês"}
                  </strong>
                </span>
              )}
            </span>
            <Button
              variant="contained"
              size="small"
              disabled={creatingInvoice}
              onClick={handleRenovar}
              style={{ background: "#e65100", color: "#fff", fontWeight: 700,
                borderRadius: 6, textTransform: "none" }}
            >
              {creatingInvoice ? "Preparando..." : "Renovar Assinatura"}
            </Button>
          </div>
        )}
        {!isCompanyExpired &&
          renewalOptions?.eligibility?.eligible !== true &&
          user?.company?.dueDate && (
          <div style={{
            backgroundColor: "#e8f5e9",
            color: "#1b5e20",
            padding: "12px 18px",
            borderRadius: "8px",
            marginTop: "10px",
            border: "1px solid #a5d6a7",
            display: "flex",
            alignItems: "center",
            gap: 10
          }}>
            <span style={{ fontSize: "1.2rem" }}>✅</span>
            <span>
              <strong>Sistema ativo até {moment(user.company.dueDate).format("DD/MM/YYYY")}</strong>
              {user?.company?.plan && (
                <span style={{ marginLeft: 8, color: "#2e7d32" }}>
                  — Plano <strong>{user.company.plan.name}</strong>
                  {" · "}
                  <strong>
                    {renewalOptions?.totalValueCents
                      ? (Number(renewalOptions.totalValueCents) / 100).toLocaleString("pt-br",{style:"currency",currency:"BRL"})
                      : parseFloat(String(user.company.plan.amount).replace(",",".")).toLocaleString("pt-br",{style:"currency",currency:"BRL"})}
                    {renewalOptions?.termMonths > 1
                      ? ` pelo período de ${renewalOptions.termMonths} meses`
                      : "/mês"}
                  </strong>
                </span>
              )}
            </span>
          </div>
        )}
      </MainHeader>

      {user?.super && (
        <div className={classes.adminContent}>
          {/* Tabs */}
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            className={classes.financeTabs}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="💰 Faturamento" />
            <Tab label="📈 Lucro estimado" />
            <Tab label={reviewPendingCount > 0 ? `🔔 Mais detalhes (${reviewPendingCount})` : "⋯ Mais detalhes"} />
          </Tabs>

          {activeTab < 2 && (
            <div className={classes.filterBar}>
              <span style={{ fontWeight: 600, fontSize: "0.85rem", color: "#555" }}>Período:</span>
              {[
                { key: "all", label: "Todos" },
                { key: "today", label: "Hoje" },
                { key: "month", label: "Este Mês" },
                { key: "lastmonth", label: "Mês Passado" },
                { key: "custom", label: "Personalizado" }
              ].map(f => (
                <button key={f.key} onClick={() => { setAdminFilter(f.key); if (f.key !== "custom") { setAdminCustomStart(""); setAdminCustomEnd(""); } }} style={{
                  padding: "5px 16px", borderRadius: 20, border: "1.5px solid",
                  borderColor: adminFilter === f.key ? "#1565c0" : "#ccc",
                  background: adminFilter === f.key ? "#1565c0" : "#fff",
                  color: adminFilter === f.key ? "#fff" : "#333",
                  fontWeight: 600, fontSize: "0.8rem", cursor: "pointer"
                }}>
                  {f.label}
                </button>
              ))}
              {adminFilter === "custom" && (
                <>
                  <input type="date" value={adminCustomStart} onChange={e => setAdminCustomStart(e.target.value)}
                    style={{ border: "1.5px solid #ccc", borderRadius: 6, padding: "4px 8px", fontSize: "0.8rem" }} />
                  <span style={{ color: "#666" }}>até</span>
                  <input type="date" value={adminCustomEnd} onChange={e => setAdminCustomEnd(e.target.value)}
                    style={{ border: "1.5px solid #ccc", borderRadius: 6, padding: "4px 8px", fontSize: "0.8rem" }} />
                </>
              )}
              {(adminLoading || overviewLoading) && <span style={{ fontSize: "0.78rem", color: "#888" }}>Carregando...</span>}
              {activeTab === 0 && (
                <>
                  <button onClick={() => setInadimplentesOnly(v => !v)} style={{
                    padding: "5px 14px", borderRadius: 20, border: "1.5px solid",
                    borderColor: inadimplentesOnly ? "#b71c1c" : "#ccc",
                    background: inadimplentesOnly ? "#b71c1c" : "#fff",
                    color: inadimplentesOnly ? "#fff" : "#333",
                    fontWeight: 600, fontSize: "0.8rem", cursor: "pointer"
                  }}>Só Inadimplentes</button>
                  <button onClick={() => setSortByValue(v => !v)} style={{
                    padding: "5px 14px", borderRadius: 20, border: "1.5px solid",
                    borderColor: sortByValue ? "#1565c0" : "#ccc",
                    background: sortByValue ? "#1565c0" : "#fff",
                    color: sortByValue ? "#fff" : "#333",
                    fontWeight: 600, fontSize: "0.8rem", cursor: "pointer"
                  }}>↓ Maior Valor</button>
                  {financialOverview?.featureEnabled === true && (
                    <button onClick={exportToCSV} style={{
                      padding: "5px 14px", borderRadius: 20, border: "1.5px solid #2e7d32",
                      background: "#2e7d32", color: "#fff",
                      fontWeight: 700, fontSize: "0.8rem", cursor: "pointer",
                      marginLeft: "auto", boxShadow: "0 2px 6px rgba(46,125,50,.22)"
                    }}>⬇ Exportar Excel</button>
                  )}
                </>
              )}
            </div>
          )}

          {/* ===== TAB 0: FATURAMENTO ===== */}
          {activeTab === 0 && (
            <>
              {financialOverview?.featureEnabled === false && (
                <div style={{ padding: 18, borderRadius: 8, background: "#fff8e1", color: "#795548" }}>
                  A visão financeira permanece protegida até a ativação do Ledger.
                </div>
              )}

              {financialOverview?.featureEnabled === true && (
                <>
                  {!revenueContractOk && (
                    <div style={{ padding: 14, borderRadius: 8, background: "#ffebee", color: "#b71c1c", fontWeight: 700, marginBottom: 16 }}>
                      Não foi possível validar os dados de faturamento. Atualize a página; nenhum total incompleto será apresentado como zero.
                    </div>
                  )}
                  {revenueContractOk && (
                  <>
                  <div className={classes.sectionTitle}>Resumo financeiro</div>
                  <Grid container spacing={1} style={{ marginBottom: 22 }}>
                    {[
                      { label: "Faturamento", value: formatCents(financialOverview.commercial?.recognizedGrossCents), icon: "💰", color: "#37474f", bg: "#fff8e1", primary: true },
                      { label: "PIX", value: `${formatCents(salesByMethod.pixCents)} (${salesByMethod.pixCount})`, icon: "🔵", color: "#1565c0", bg: "#e3f2fd" },
                      { label: "Cartão", value: `${formatCents(salesByMethod.cardCents)} (${salesByMethod.cardCount})`, icon: "💳", color: "#6a1b9a", bg: "#f3e5f5" },
                      { label: `Inadimplentes (${overdueClientsCount})`, value: formatCents(financialOverview.commercial?.overdueCents), icon: "⚠️", color: "#b71c1c", bg: "#ffebee" }
                    ].map((card, i) => (
                      <Grid item xs={12} sm={6} md key={i}>
                        <div className={classes.summaryCard} style={{ background: card.bg, border: `1px solid ${card.color}26` }}>
                          <div style={{ fontSize: "1.4rem", marginBottom: 4 }}>{card.icon}</div>
                          <div style={{ fontSize: "0.72rem", color: "#666", fontWeight: 500, marginBottom: 4 }}>{card.label}</div>
                          <div style={{ fontSize: card.primary ? "1.35rem" : "1.2rem", fontWeight: 800, color: card.color }}>{card.value}</div>
                        </div>
                      </Grid>
                    ))}
                  </Grid>
                  {Number(financialForecast.needsReviewGrossCents || 0) > 0 && (
                    <div style={{ padding: "10px 14px", borderRadius: 8, background: "#f3e5f5", color: "#6a1b9a", fontSize: "0.8rem", marginBottom: 18 }}>
                      Há {formatCents(financialForecast.needsReviewGrossCents)} aprovado(s) no histórico aguardando conciliação de evidência do provedor. O valor permanece identificado para revisão.
                    </div>
                  )}
                  </>
                  )}

                  <div className={classes.sectionTitle}>
                    📋 Todas as Faturas ({getDisplayPurchases().length})
                  </div>
                  <div className={classes.tableSurface}>
                  <Table size="small" style={{ minWidth: 1080, tableLayout: "auto" }}>
                    <TableHead>
                      <TableRow style={{ background: "#f5f5f5" }}>
                        <TableCell><strong>Empresa</strong></TableCell>
                        <TableCell><strong>Plano</strong></TableCell>
                        <TableCell align="right"><strong>Valor</strong></TableCell>
                        <TableCell align="center"><strong>Vencimento</strong></TableCell>
                        <TableCell align="center"><strong>Pagamento</strong></TableCell>
                        <TableCell align="center"><strong>Método</strong></TableCell>
                        <TableCell><strong>Email</strong></TableCell>
                        <TableCell><strong>Telefone</strong></TableCell>
                        <TableCell align="center"><strong>Status</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getDisplayPurchases().map(purchase => {
                        const colors = purchaseStatusColors(purchase.presentationStatus);
                        return (
                          <React.Fragment key={purchase.uuid}>
                            <TableRow style={{ borderBottom: "1px solid #eee" }}>
                              <TableCell style={{ fontWeight: 600 }}>{purchase.company?.name || purchase.companyId}</TableCell>
                              <TableCell>{purchase.planName || "-"}</TableCell>
                              <TableCell align="right" style={{ fontWeight: 700 }}>{formatCents(purchase.totalValueCents)}</TableCell>
                              <TableCell align="center">{purchase.payments?.[0]?.dueDate ? moment(purchase.payments[0].dueDate).format("DD/MM/YYYY") : "-"}</TableCell>
                              <TableCell align="center">{purchase.approvedAt ? moment(purchase.approvedAt).format("DD/MM/YYYY") : "-"}</TableCell>
                              <TableCell align="center">
                                <span className={classes.statusPill} style={{ color: "#0e6471", background: "#e0f2f1" }}>
                                  {String(purchase.billingType || "").toUpperCase() === "PIX" ? "PIX" : `${purchase.selectedInstallments}x Cartão`}
                                </span>
                              </TableCell>
                              <TableCell>{purchase.company?.email || "-"}</TableCell>
                              <TableCell>{purchase.company?.phone || "-"}</TableCell>
                              <TableCell align="center">
                                <span className={classes.statusPill} style={colors}>
                                  {purchaseStatusLabel(purchase.presentationStatus)}
                                </span>

                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                  </div>
                </>
              )}

              {false && adminData && (
                <div style={{ marginTop: 26 }}>
                  <div style={{ fontWeight: 700, fontSize: "1rem", marginBottom: 10, color: "#333" }}>
                    📄 {inadimplentesOnly ? "Faturas mensais inadimplentes" : "Faturas mensais legadas"} ({getDisplayInvoices().length})
                  </div>
                  <div className={classes.tableSurface}>
                  <Table size="small" style={{ minWidth: 1040 }}>
                    <TableHead>
                      <TableRow style={{ background: "#f5f5f5" }}>
                        <TableCell><strong>Empresa</strong></TableCell>
                        <TableCell><strong>Plano</strong></TableCell>
                        <TableCell align="right"><strong>Valor</strong></TableCell>
                        <TableCell align="center"><strong>Vencimento</strong></TableCell>
                        <TableCell align="center"><strong>Pagamento</strong></TableCell>
                        <TableCell align="center"><strong>Método</strong></TableCell>
                        <TableCell><strong>Email</strong></TableCell>
                        <TableCell><strong>Telefone</strong></TableCell>
                        <TableCell align="center"><strong>Status legado</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getDisplayInvoices().map(inv => (
                        <TableRow key={inv.id}>
                          <TableCell>{inv.company?.name || inv.companyId}</TableCell>
                          <TableCell>{inv.company?.plan?.name || inv.detail || "-"}</TableCell>
                          <TableCell align="right">{Number(inv.value || 0).toLocaleString("pt-br", { style: "currency", currency: "BRL" })}</TableCell>
                          <TableCell align="center">{inv.dueDate ? moment(inv.dueDate).format("DD/MM/YYYY") : "-"}</TableCell>
                          <TableCell align="center">{inv.payedAt ? moment(inv.payedAt).format("DD/MM/YYYY") : "-"}</TableCell>
                          <TableCell align="center">
                            <span className={classes.statusPill} style={{ color: "#0e6471", background: "#e0f2f1" }}>
                              {inv.paymentMethod || "-"}
                            </span>
                          </TableCell>
                          <TableCell>{inv.company?.email || "-"}</TableCell>
                          <TableCell>{inv.company?.phone || "-"}</TableCell>
                          <TableCell align="center">
                            <span className={classes.statusPill} style={inv.status === "paid"
                              ? { color: "#1b5e20", background: "#e8f5e9" }
                              : { color: "#b54708", background: "#fff3e0" }}>
                              {inv.status === "paid" ? "✓ Pago" : "⚠ Em aberto"}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ===== SECAO: ENTRADAS DO MES NA TELA PRINCIPAL ===== */}
          {false && activeTab === 0 && (
            <>
              <div style={{ fontWeight: 800, fontSize: "1rem", margin: "26px 0 10px", color: "#263238" }}>
                Entradas do mês — o que realmente entrou
              </div>
              <div style={{ padding: "12px 16px", borderRadius: 8, background: "#e8f5e9", color: "#1b5e20", fontSize: "0.82rem", marginBottom: 16 }}>
                Aqui aparece somente o dinheiro que realmente entrou em <strong>{adminPeriodLabel}</strong>. Uma venda parcelada entra aos poucos, conforme cada parcela é creditada pelo Asaas.
              </div>

              <Grid container spacing={2} style={{ marginBottom: 24 }}>
                {[
                  { label: "Entradas brutas", value: lucro.gross, icon: "🏦", color: "#1565c0", bg: "#e3f2fd", minus: false },
                  { label: "Taxas do provedor", value: lucro.providerFee, icon: "💳", color: "#455a64", bg: "#eceff1", minus: true },
                  { label: "Entrada líquida no caixa", value: lucro.cashNet, icon: "✅", color: "#00695c", bg: "#e0f2f1", minus: false, bold: true }
                ].map((card, i) => (
                  <Grid item xs={12} sm={4} key={i}>
                    <div style={{ background: card.bg, border: `1px solid ${card.color}33`, borderRadius: 10, padding: "14px 16px" }}>
                      <div style={{ fontSize: "1.4rem", marginBottom: 4 }}>{card.icon}</div>
                      <div style={{ fontSize: "0.72rem", color: "#666", fontWeight: 500, marginBottom: 4 }}>{card.label}</div>
                      <div style={{ fontSize: card.bold ? "1.3rem" : "1.1rem", fontWeight: 800, color: card.color }}>
                        {card.minus && "- "}R$ {Math.abs(card.value).toLocaleString("pt-br",{minimumFractionDigits:2})}
                      </div>
                    </div>
                  </Grid>
                ))}
              </Grid>
              {lucro.unknownCashCount > 0 && (
                <div style={{ padding: "10px 14px", borderRadius: 8, background: "#fff8e1", color: "#795548", fontSize: "0.8rem", marginBottom: 20 }}>
                  O caixa líquido usa o netValue real do Asaas quando disponível.
                  {` Há ${lucro.unknownCashCount} recebimento(s) legado(s) sem netValue; a taxa configurada é usada somente nesses casos.`}
                </div>
              )}

              <div style={{ fontWeight: 700, fontSize: "1rem", marginBottom: 10, color: "#333" }}>
                🏦 Entradas identificadas por contratação ({purchasesWithCashInPeriod.length})
              </div>
              {purchasesWithCashInPeriod.length === 0 ? (
                <div style={{ padding: 20, border: "1px solid #e0e0e0", borderRadius: 8, color: "#666", background: "#fafafa" }}>
                  Nenhuma parcela de contratação foi creditada neste período.
                </div>
              ) : (
                <Table size="small" style={{ background: "#fff", borderRadius: 8, overflow: "hidden" }}>
                  <TableHead>
                    <TableRow style={{ background: "#f5f5f5" }}>
                      <TableCell><strong>Empresa</strong></TableCell>
                      <TableCell><strong>Plano</strong></TableCell>
                      <TableCell align="right"><strong>Entrada bruta no período</strong></TableCell>
                      <TableCell align="right"><strong>Recebido desde a contratação</strong></TableCell>
                      <TableCell align="center"><strong>Créditos do período</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {purchasesWithCashInPeriod.map(purchase => {
                      const periodOperations =
                        purchase.cashOperationsInPeriod || [];
                      return (
                        <TableRow key={purchase.uuid}>
                          <TableCell>{purchase.company?.name || purchase.companyId}</TableCell>
                          <TableCell>{purchase.planName || "-"}</TableCell>
                          <TableCell align="right" style={{ fontWeight: 800, color: "#1565c0" }}>
                            {formatCents(purchase.receivedGrossInPeriodCents)}
                          </TableCell>
                          <TableCell align="right">{formatCents(purchase.receivedGrossToDateCents)}</TableCell>
                          <TableCell align="center">
                            {periodOperations.length > 0
                              ? periodOperations.map((operation, index) => (
                                  <div key={`${operation.entryType}-${operation.occurredOn}-${index}`} style={{ fontSize: "0.75rem", marginBottom: 3 }}>
                                    {operation.entryType === "ANTICIPATION_CREDIT"
                                      ? `Antecipação Asaas · ${operation.linkedPaymentCount} parcela(s)`
                                      : operation.entryType === "ANTICIPATION_DEBIT_ADJUSTMENT"
                                      ? "Ajuste de antecipação Asaas"
                                      : "Crédito de parcela"}
                                    {" em "}
                                    {moment(operation.occurredOn).format("DD/MM/YYYY")}
                                  </div>
                                ))
                              : "Registrado no histórico financeiro"}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
              <div style={{ fontSize: "0.75rem", color: "#777", marginTop: 10 }}>
                Os totais acima também incluem recebimentos mensais legados conciliados no período, mesmo quando não pertencem a uma contratação parcelada.
              </div>
            </>
          )}

          {/* ===== TAB 2: LUCRO ESTIMADO ===== */}
          {activeTab === 1 && (
            <>
              {!revenueContractOk && (
                <div style={{ padding: 14, borderRadius: 8, background: "#ffebee", color: "#b71c1c", fontWeight: 700, marginBottom: 16 }}>
                  Não foi possível validar os dados de lucro estimado. Atualize a página; nenhum total incompleto será apresentado como zero.
                </div>
              )}
              {revenueContractOk && (
              <>
              <div style={{ padding: "12px 16px", borderRadius: 8, background: "#e3f2fd", color: "#0d47a1", fontSize: "0.82rem", marginBottom: 16 }}>
                Esta é uma projeção comercial de <strong>{adminPeriodLabel}</strong>, reconhecida quando o PIX é recebido ou o cartão é confirmado. Saldo disponível, datas de crédito e antecipações continuam sendo conferidos diretamente no Asaas.
              </div>
              <Grid container spacing={2} style={{ marginBottom: 24 }}>
                {[
                  { label: "Faturamento total", value: lucro.gross, icon: "💰", color: "#1565c0", bg: "#e3f2fd", minus: false },
                  { label: "Taxas configuradas", value: lucro.providerFee, icon: "💳", color: "#6a1b9a", bg: "#f3e5f5", minus: true },
                  ...(lucro.adjustments < 0 ? [{ label: "Estornos/chargebacks", value: lucro.adjustments, icon: "↩️", color: "#ad1457", bg: "#fce4ec", minus: true }] : []),
                  { label: "Receita após taxas", value: lucro.cashNet, icon: "📈", color: "#1b5e20", bg: "#e8f5e9", minus: false, bold: true },
                  { label: `Impostos (${parseConfigNumber(financialConfig.taxPercent, 100).toLocaleString("pt-br")}% + ${parseConfigNumber(financialConfig.taxFixed).toLocaleString("pt-br",{style:"currency",currency:"BRL"})})`, value: lucro.tax, icon: "🏛️", color: "#b71c1c", bg: "#ffebee", minus: true },
                  { label: "Custos extras", value: lucro.extraTotal, icon: "📦", color: "#e65100", bg: "#fff3e0", minus: true },
                  { label: "Resultado previsto após custos", value: lucro.net, icon: lucro.net >= 0 ? "📊" : "📉", color: lucro.net >= 0 ? "#1b5e20" : "#b71c1c", bg: lucro.net >= 0 ? "#e8f5e9" : "#ffebee", minus: false, bold: true }
                ].map((card, i) => (
                  <Grid item xs={12} sm={6} md key={i}>
                    <div className={classes.summaryCard} style={{ background: card.bg, border: `1px solid ${card.color}26` }}>
                      <div style={{ fontSize: "1.4rem", marginBottom: 4 }}>{card.icon}</div>
                      <div style={{ fontSize: "0.72rem", color: "#666", fontWeight: 500, marginBottom: 4 }}>{card.label}</div>
                      <div style={{ fontSize: card.bold ? "1.3rem" : "1.1rem", fontWeight: 800, color: card.color }}>
                        {card.minus && "- "}R$ {Math.abs(card.value).toLocaleString("pt-br",{minimumFractionDigits:2})}
                      </div>
                    </div>
                  </Grid>
                ))}
              </Grid>
              <div style={{ padding: "10px 14px", borderRadius: 8, background: "#fff8e1", color: "#795548", fontSize: "0.8rem", marginBottom: 20 }}>
                O lucro estimado usa o faturamento reconhecido e desconta exatamente uma vez as taxas configuradas abaixo. A taxa real do Asaas é exibida apenas para conciliação e nunca é descontada novamente. Estornos e chargebacks reduzem o valor. Impostos e custos mensais são aplicados uma vez por mês do período ({lucro.periodMonths} mês(es)).
              </div>
              <div style={{ padding: "12px 16px", borderRadius: 8, background: "#f5f5f5", color: "#455a64", fontSize: "0.8rem", marginBottom: 20 }}>
                <strong>Conciliação das taxas:</strong>{" "}
                {financialForecast.feeReconciliation?.matchedCount || 0} conferida(s),{" "}
                {financialForecast.feeReconciliation?.pendingCount || 0} aguardando liquidação · configurada{" "}
                <strong>{formatCents(financialForecast.feeReconciliation?.configuredCents)}</strong> · real Asaas{" "}
                <strong>{formatCents(financialForecast.feeReconciliation?.actualCents)}</strong> · diferença{" "}
                <strong>{formatCents(financialForecast.feeReconciliation?.differenceCents)}</strong>
              </div>

              {/* Config Section */}
              <div style={{ background: "#fafafa", border: "1px solid #e0e0e0", borderRadius: 10, padding: "20px 24px", marginBottom: 20 }}>
                <div style={{ fontWeight: 700, fontSize: "1rem", marginBottom: 16, color: "#333" }}>⚙️ Configurar custos do lucro estimado</div>
                <div style={{ padding: "10px 12px", borderRadius: 8, background: "#e3f2fd", color: "#0d47a1", fontSize: "0.78rem", marginBottom: 18 }}>
                  Você pode usar vírgula ou ponto. Imposto, PIX e cartão aceitam percentual e valor fixo ao mesmo tempo; os dois são somados no cálculo após salvar.
                </div>

                <Grid container spacing={3}>
                  {/* Impostos */}
                  <Grid item xs={12} sm={4}>
                    <div style={{ fontWeight: 600, fontSize: "0.85rem", color: "#555", marginBottom: 8 }}>🏛️ Impostos</div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <TextField
                        label="% sobre bruto"
                        type="text"
                        size="small"
                        variant="outlined"
                        value={financialConfig.taxPercent}
                        onChange={e => { const v = e.target.value; setFinancialConfig(p => ({...p, taxPercent: v})); }}
                        style={{ width: 130 }}
                        inputProps={{ inputMode: "decimal" }}
                      />
                      <span style={{ color: "#aaa" }}>+</span>
                      <TextField
                        label="R$ fixo/mês"
                        type="text"
                        size="small"
                        variant="outlined"
                        value={financialConfig.taxFixed}
                        onChange={e => { const v = e.target.value; setFinancialConfig(p => ({...p, taxFixed: v})); }}
                        style={{ width: 130 }}
                        inputProps={{ inputMode: "decimal" }}
                      />
                    </div>
                  </Grid>

                  {/* Taxa PIX */}
                  <Grid item xs={12} sm={4}>
                    <div style={{ fontWeight: 600, fontSize: "0.85rem", color: "#555", marginBottom: 8 }}>🔵 Taxa PIX</div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <TextField
                        label="% por transação"
                        type="text"
                        size="small"
                        variant="outlined"
                        value={financialConfig.pixFeePercent}
                        onChange={e => { const v = e.target.value; setFinancialConfig(p => ({...p, pixFeePercent: v})); }}
                        style={{ width: 130 }}
                        inputProps={{ inputMode: "decimal" }}
                      />
                      <span style={{ color: "#aaa" }}>+</span>
                      <TextField
                        label="R$ fixo/transação"
                        type="text"
                        size="small"
                        variant="outlined"
                        value={financialConfig.pixFeeFixed}
                        onChange={e => { const v = e.target.value; setFinancialConfig(p => ({...p, pixFeeFixed: v})); }}
                        style={{ width: 130 }}
                        inputProps={{ inputMode: "decimal" }}
                      />
                    </div>
                  </Grid>

                  {/* Taxa Cartão */}
                  <Grid item xs={12} sm={4}>
                    <div style={{ fontWeight: 600, fontSize: "0.85rem", color: "#555", marginBottom: 8 }}>💳 Taxa Cartão</div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <TextField
                        label="% por transação"
                        type="text"
                        size="small"
                        variant="outlined"
                        value={financialConfig.cardFeePercent}
                        onChange={e => { const v = e.target.value; setFinancialConfig(p => ({...p, cardFeePercent: v})); }}
                        style={{ width: 130 }}
                        inputProps={{ inputMode: "decimal" }}
                      />
                      <span style={{ color: "#aaa" }}>+</span>
                      <TextField
                        label="R$ fixo/transação"
                        type="text"
                        size="small"
                        variant="outlined"
                        value={financialConfig.cardFeeFixed}
                        onChange={e => { const v = e.target.value; setFinancialConfig(p => ({...p, cardFeeFixed: v})); }}
                        style={{ width: 130 }}
                        inputProps={{ inputMode: "decimal" }}
                      />
                    </div>
                  </Grid>

                  {/* Custos Extras */}
                  <Grid item xs={12}>
                    <div style={{ fontWeight: 600, fontSize: "0.85rem", color: "#555", marginBottom: 8 }}>📦 Custos Extras (mensais fixos)</div>
                    {(financialConfig.extraCosts || []).map((cost, idx) => (
                      <div key={idx} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                        <span style={{ minWidth: 180, fontSize: "0.85rem", color: "#333" }}>{cost.name}</span>
                        <span style={{ fontWeight: 700, color: "#e65100", fontSize: "0.9rem" }}>
                          R$ {Number(cost.value||0).toLocaleString("pt-br",{minimumFractionDigits:2})}
                        </span>
                        <IconButton size="small" onClick={() => handleRemoveExtraCost(idx)} style={{ color: "#b71c1c" }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </div>
                    ))}
                    <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
                      <TextField
                        label="Nome do custo"
                        size="small"
                        variant="outlined"
                        value={newCostName}
                        onChange={e => { const v = e.target.value; setNewCostName(v); }}
                        style={{ width: 200 }}
                      />
                      <TextField
                        label="Valor (R$)"
                        type="text"
                        size="small"
                        variant="outlined"
                        value={newCostValue}
                        onChange={e => { const v = e.target.value; setNewCostValue(v); }}
                        style={{ width: 120 }}
                        inputProps={{ inputMode: "decimal" }}
                      />
                      <IconButton size="small" onClick={handleAddExtraCost} style={{ color: "#1565c0" }}>
                        <AddCircleIcon />
                      </IconButton>
                    </div>
                  </Grid>
                </Grid>

                <div style={{ marginTop: 20 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={configLoading}
                    onClick={handleSaveConfig}
                    style={{ textTransform: "none", fontWeight: 700, borderRadius: 8 }}
                  >
                    {configLoading ? "Salvando..." : "💾 Salvar Configurações"}
                  </Button>
                </div>
              </div>
              </>
              )}
            </>
          )}

          {activeTab === 2 && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ padding: "12px 16px", borderRadius: 8, background: "#f5f5f5", color: "#455a64", fontSize: "0.82rem", marginBottom: 12 }}>
                Informações de auditoria e situações que precisam de análise ficam aqui, sem misturar com a visão principal do financeiro.
              </div>
              <Tabs
                value={detailTab}
                onChange={(e, v) => setDetailTab(v)}
                indicatorColor="primary"
                textColor="primary"
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab label="📚 Histórico financeiro" />
                <Tab label={reviewPendingCount > 0 ? `🔔 Pendências financeiras (${reviewPendingCount})` : "🛡️ Pendências financeiras"} />
              </Tabs>
            </div>
          )}

          {/* ===== MAIS DETALHES: HISTORICO FINANCEIRO ===== */}
          {activeTab === 2 && detailTab === 0 && (
            <>
              {financialOverview?.anticipationReconciliation && (
                <div style={{
                  padding: "12px 16px",
                  borderRadius: 8,
                  background:
                    financialOverview.anticipationReconciliation.status === "COMPLETED"
                      ? "#e8f5e9"
                      : "#fff3e0",
                  color:
                    financialOverview.anticipationReconciliation.status === "COMPLETED"
                      ? "#1b5e20"
                      : "#e65100",
                  fontSize: "0.8rem",
                  marginBottom: 12
                }}>
                  <strong>Conciliação automática de antecipações:</strong>
                  {" "}
                  {financialOverview.anticipationReconciliation.status}
                  {" · "}
                  {financialOverview.anticipationReconciliation.completedAt
                    ? moment(financialOverview.anticipationReconciliation.completedAt).format("DD/MM/YYYY HH:mm")
                    : "em andamento"}
                  {" · "}
                  {financialOverview.anticipationReconciliation.localMatches || 0} operação(ões) vinculada(s)
                  {" · "}
                  {financialOverview.anticipationReconciliation.recovered || 0} recuperada(s)
                  {" · webhook "}
                  {financialOverview.anticipationReconciliation.webhookHealthy ? "ativo" : "requer atenção"}
                </div>
              )}
              {ledgerLoading && <div style={{ color: "#777" }}>Carregando histórico financeiro...</div>}
              {ledgerData?.featureEnabled === false && (
                <div style={{ padding: 18, borderRadius: 8, background: "#fff8e1", color: "#795548" }}>
                  O histórico financeiro permanece protegido e ainda não está habilitado para consulta.
                </div>
              )}
              {ledgerData?.featureEnabled === true && (
                <>
                  <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
                    <Button variant="outlined" color="primary" onClick={handleLedgerExport}>
                      Exportar histórico CSV
                    </Button>
                  </div>
                  <Grid container spacing={2} style={{ marginBottom: 20 }}>
                    {[
                      ["Contratado", ledgerData.summary?.contractedCents, "#1565c0", "#e3f2fd"],
                      ["Confirmado aguardando crédito", ledgerData.summary?.awaitingCreditCents, "#e65100", "#fff3e0"],
                      ["Recebido bruto", ledgerData.summary?.receivedGrossCents, "#2e7d32", "#e8f5e9"],
                      ["Recebido líquido conhecido", ledgerData.summary?.receivedNetKnownCents, "#00695c", "#e0f2f1"],
                      ["A receber", ledgerData.summary?.receivableCents, "#6a1b9a", "#f3e5f5"],
                      ["Atrasado", ledgerData.summary?.overdueCents, "#b71c1c", "#ffebee"],
                      ["Taxas do provedor", ledgerData.summary?.feeCents, "#455a64", "#eceff1"],
                      ["Estornos/chargebacks", ledgerData.summary?.adjustmentsCents, "#ad1457", "#fce4ec"]
                    ].map(([label, value, color, bg]) => (
                      <Grid item xs={12} sm={6} md={3} key={label}>
                        <div style={{ background: bg, border: `1px solid ${color}33`, borderRadius: 10, padding: "14px 16px" }}>
                          <div style={{ fontSize: "0.75rem", color: "#666", fontWeight: 600 }}>{label}</div>
                          <div style={{ fontSize: "1.15rem", fontWeight: 800, color, marginTop: 5 }}>{formatCents(value)}</div>
                        </div>
                      </Grid>
                    ))}
                  </Grid>
                  {Number(ledgerData.summary?.netUnknownCount || 0) > 0 && (
                    <div style={{ fontSize: "0.78rem", color: "#795548", marginBottom: 10 }}>
                      {ledgerData.summary.netUnknownCount} recebimento(s) legado(s) sem netValue real; nenhum valor líquido foi inventado.
                    </div>
                  )}
                  <Table size="small" style={{ background: "#fff" }}>
                    <TableHead>
                      <TableRow style={{ background: "#f5f5f5" }}>
                        <TableCell>Data</TableCell>
                        <TableCell>Empresa</TableCell>
                        <TableCell>Tipo</TableCell>
                        <TableCell>Status origem</TableCell>
                        <TableCell align="right">Bruto</TableCell>
                        <TableCell align="right">Líquido</TableCell>
                        <TableCell align="right">Taxa</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(ledgerData.entries || []).map(entry => (
                        <TableRow key={entry.uuid}>
                          <TableCell>{moment(entry.occurredOn).format("DD/MM/YYYY")}</TableCell>
                          <TableCell>{entry.company?.name || entry.companyId}</TableCell>
                          <TableCell>{entry.entryType}</TableCell>
                          <TableCell>{entry.sourceStatus}</TableCell>
                          <TableCell align="right">{formatCents(entry.grossValueCents)}</TableCell>
                          <TableCell align="right">{entry.netValueCents == null ? "não informado" : formatCents(entry.netValueCents)}</TableCell>
                          <TableCell align="right">{entry.feeValueCents == null ? "-" : formatCents(entry.feeValueCents)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </>
          )}

          {/* ===== MAIS DETALHES: PENDENCIAS FINANCEIRAS ===== */}
          {activeTab === 2 && detailTab === 1 && (
            <>
              {reviewLoading && <div style={{ color: "#777" }}>Carregando pendências financeiras...</div>}
              {reviewData?.featureEnabled === false && (
                <div style={{ padding: 18, borderRadius: 8, background: "#fff8e1", color: "#795548" }}>
                  A fila de pendências financeiras permanece protegida e ainda não está habilitada para consulta.
                </div>
              )}
              {reviewData?.featureEnabled === true && (
                <>
                  {reviewPendingCount > 0 ? (
                    <div style={{ padding: "12px 16px", borderRadius: 8, background: "#ffebee", color: "#b71c1c", fontWeight: 700, marginBottom: 12 }}>
                      🔔 {reviewPendingCount} pendência(s) financeira(s) exigem decisão humana.
                    </div>
                  ) : (
                    <div style={{ padding: "12px 16px", borderRadius: 8, background: "#e8f5e9", color: "#1b5e20", fontWeight: 700, marginBottom: 12 }}>
                      Nenhuma pendência financeira aguardando análise.
                    </div>
                  )}
                  <Table size="small" style={{ background: "#fff" }}>
                    <TableHead>
                      <TableRow style={{ background: "#f5f5f5" }}>
                        <TableCell>Data</TableCell>
                        <TableCell>Empresa</TableCell>
                        <TableCell>Motivo</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Acesso já concedido</TableCell>
                        <TableCell>Decisão controlada</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(reviewData.items || []).map(item => (
                        <TableRow key={item.uuid}>
                          <TableCell>{moment(item.createdAt).format("DD/MM/YYYY HH:mm")}</TableCell>
                          <TableCell>{item.company?.name || item.companyId}</TableCell>
                          <TableCell>
                            <strong>{item.reasonCode}</strong>
                            <div style={{ fontSize: "0.7rem", color: "#777" }}>{item.sourceCode}</div>
                          </TableCell>
                          <TableCell>{item.status}</TableCell>
                          <TableCell>{item.accessAlreadyGranted ? "Sim — nunca revogar automaticamente" : "Não"}</TableCell>
                          <TableCell>
                            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                              <select
                                value={reviewActions[item.uuid] || "KEEP_ACCESS"}
                                onChange={e => setReviewActions(prev => ({ ...prev, [item.uuid]: e.target.value }))}
                                style={{ padding: "6px 8px", borderRadius: 6, border: "1px solid #ccc" }}
                              >
                                <option value="KEEP_ACCESS">Manter acesso</option>
                                <option value="SUSPEND_ACCESS">Solicitar suspensão</option>
                                <option value="ADJUST_PERIOD">Solicitar ajuste de período</option>
                                <option value="CHARGE_AGAIN">Cobrar novamente</option>
                                <option value="RECORD_LOSS">Registrar perda</option>
                                <option value="ESCALATE">Escalar</option>
                              </select>
                              <Button size="small" variant="outlined" color="primary" onClick={() => handleReviewDecision(item)}>
                                Registrar
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </>
          )}
        </div>
      )}

      {!user?.super && (
      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">Detalhes</TableCell>
              <TableCell align="center">Usuários</TableCell>
              <TableCell align="center">Conexões</TableCell>
              <TableCell align="center">Filas</TableCell>
              <TableCell align="center">Valor</TableCell>
              <TableCell align="center">Data Venc.</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Ação</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {invoices.map((invoices) => (
                <TableRow style={rowStyle(invoices)} key={invoices.id}>
                  <TableCell align="center">{invoices.detail}</TableCell>
                  <TableCell align="center">{invoices.users}</TableCell>
                  <TableCell align="center">{invoices.connections}</TableCell>
                  <TableCell align="center">{invoices.queues}</TableCell>
                  <TableCell style={{ fontWeight: 'bold' }} align="center">{invoices.value.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}</TableCell>
                  <TableCell align="center">{moment(invoices.dueDate).format("DD/MM/YYYY")}</TableCell>
                  <TableCell style={{ fontWeight: 'bold' }} align="center">
                    {approvedInstallmentMessage(invoices) ? (
                      <div>
                        <div style={{ color: "#1b5e20" }}>Pagamento aprovado</div>
                        <div style={{ fontSize: "0.72rem", fontWeight: 500, color: "#2e7d32", marginTop: 3 }}>
                          {approvedInstallmentMessage(invoices)}
                        </div>
                      </div>
                    ) : rowStatus(invoices)}
                  </TableCell>
                  <TableCell align="center">
                    {approvedInstallmentMessage(invoices) ? (
                      <Button
                        size="small"
                        variant="outlined"
                        disabled
                        style={{ color: "#1b5e20", borderColor: "#81c784" }}
                      >
                        APROVADO
                      </Button>
                    ) : rowStatus(invoices) !== "Pago" ?
                      <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        onClick={() => handleOpenContactModal(invoices)}
                      >
                        PAGAR
                      </Button> :
                      <Button
                        size="small"
                        variant="outlined"
                      >
                        PAGO
                      </Button>}
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={4} />}
            </>
          </TableBody>
        </Table>
      </Paper>
      )}
    </MainContainer>
  );
};

export default Invoices;
